import type { ApiQuote, ApiJob, ApiInvoice } from './apiTypes'
import { computeWorkStage } from './workStage'
import type { WorkStage } from './workStage'

export type { WorkStage }

export interface WorkItem {
  id: string
  quote?: ApiQuote
  job?: ApiJob
  invoice?: ApiInvoice
  stage: WorkStage
  customer: string
  total: number
  updatedAt: string
}

function contactName(entity?: {
  contact?: { id: string; firstName: string; lastName: string }
}): string | null {
  if (!entity?.contact) return null
  return `${entity.contact.firstName} ${entity.contact.lastName}`.trim()
}

/** What a job is worth: its quote's total (from the quote list, else the job payload). 0 with no quote. */
export function jobValue(job: ApiJob, quote?: ApiQuote): number {
  return quote?.total ?? job.quote?.total ?? job.total ?? 0
}

export function zipWorkItems(
  quotes: ApiQuote[],
  jobs: ApiJob[],
  invoices: ApiInvoice[]
): WorkItem[] {
  const items: WorkItem[] = []
  const usedJobIds = new Set<string>()
  const usedQuoteIds = new Set<string>()

  // Build lookup maps
  const jobById = new Map<string, ApiJob>(jobs.map((j) => [j.id, j]))
  const quoteById = new Map<string, ApiQuote>(quotes.map((q) => [q.id, q]))

  // 1. Start from invoices — walk backward to find linked job + quote
  for (const invoice of invoices) {
    const job = invoice.jobId ? jobById.get(invoice.jobId) : undefined
    // The job's quote, or the quote the invoice was raised from directly
    // (otherwise that quote stayed on the board as a second, "Quoted" card).
    const quoteId = job?.quoteId ?? invoice.quoteId ?? undefined
    const quote = quoteId ? quoteById.get(quoteId) : undefined

    if (job) usedJobIds.add(job.id)
    if (quote) usedQuoteIds.add(quote.id)

    const customer =
      contactName(invoice) ??
      contactName(job) ??
      contactName(quote) ??
      'Unknown'

    const stage = computeWorkStage({ quote, job, invoice })

    items.push({
      id: invoice.id,
      invoice,
      job,
      quote,
      stage,
      customer,
      total: invoice.total ?? 0,
      updatedAt: invoice.updatedAt,
    })
  }

  // 2. Orphan jobs (no invoice linked to them)
  for (const job of jobs) {
    if (usedJobIds.has(job.id)) continue
    const quote = job.quoteId ? quoteById.get(job.quoteId) : undefined
    if (quote) usedQuoteIds.add(quote.id)

    const customer = contactName(job) ?? contactName(quote) ?? 'Unknown'
    const stage = computeWorkStage({ quote, job })

    items.push({
      id: job.id,
      job,
      quote,
      stage,
      customer,
      // A job is worth its quote (jobs carry no total of their own).
      total: jobValue(job, quote),
      updatedAt: job.updatedAt,
    })
  }

  // 3. Orphan quotes (no job, no invoice)
  for (const quote of quotes) {
    if (usedQuoteIds.has(quote.id)) continue
    const customer = contactName(quote) ?? 'Unknown'
    const stage = computeWorkStage({ quote })

    items.push({
      id: quote.id,
      quote,
      stage,
      customer,
      total: quote.total ?? 0,
      updatedAt: quote.updatedAt,
    })
  }

  return items
}
